import type { ShippingAddress } from "@/hooks/usePurchasedProductDetail";
import {
  COUNTRIES,
  US_STATES,
  findCountryByName,
  findUsStateByName,
  isUsCountry,
  validatePhone,
  type Country,
} from "@/lib/addressData";
import type { StayAiShippingAddressInput } from "@/lib/api";
import { CircleCheck, Loader2, Pencil, X } from "lucide-react";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { SuggestionInput, type SuggestionItem } from "../ui/suggestion-input";
import { OUTLINE_BRAND_BTN_CLASS } from "./constants";

export interface ReactivateSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productTitle: string;
  initialAddress: ShippingAddress | null;
  onConfirm: (payload: { address?: StayAiShippingAddressInput }) => void;
  confirmPending: boolean;
  confirmSuccess: boolean;
}

type FormState = {
  first_name: string;
  last_name: string;
  address1: string;
  address2: string;
  city: string;
  country: string;
  country_code: string;
  province: string;
  province_code: string;
  zip: string;
  phone: string;
};

const EMPTY_FORM: FormState = {
  first_name: "",
  last_name: "",
  address1: "",
  address2: "",
  city: "",
  country: "",
  country_code: "",
  province: "",
  province_code: "",
  zip: "",
  phone: "",
};

const COUNTRY_ITEMS: SuggestionItem[] = COUNTRIES.map((c) => ({
  value: c.code,
  label: c.name,
}));

const US_STATE_ITEMS: SuggestionItem[] = US_STATES.map((s) => ({
  value: s.code,
  label: s.name,
}));

function addressToForm(a: ShippingAddress | null): FormState {
  if (!a) return { ...EMPTY_FORM };
  return {
    first_name: a.first_name ?? "",
    last_name: a.last_name ?? "",
    address1: a.address1 ?? "",
    address2: a.address2 ?? "",
    city: a.city ?? "",
    country: a.country ?? "",
    country_code: a.country_code ?? "",
    province: a.province ?? "",
    province_code: a.province_code ?? "",
    zip: a.zip ?? "",
    phone: a.phone ?? "",
  };
}

function resolveCountry(form: FormState): Country | null {
  const byName = findCountryByName(form.country);
  if (byName) return byName;
  const byCode = form.country_code
    ? (COUNTRIES.find(
        (c) => c.code.toLowerCase() === form.country_code.trim().toLowerCase(),
      ) ?? null)
    : null;
  return byCode;
}

type FieldName =
  | "first_name"
  | "last_name"
  | "address1"
  | "city"
  | "country"
  | "province"
  | "province_code"
  | "zip"
  | "phone";

type FieldErrors = Partial<Record<FieldName, string>>;

function computeErrors(
  f: FormState,
  country: Country | null,
  countryIsUs: boolean,
): FieldErrors {
  const errs: FieldErrors = {};
  if (!f.first_name.trim()) errs.first_name = "Required";
  if (!f.last_name.trim()) errs.last_name = "Required";
  if (!f.address1.trim()) errs.address1 = "Required";
  if (!f.city.trim()) errs.city = "Required";
  if (!f.country.trim()) errs.country = "Required";
  else if (!country) errs.country = "Please choose a country from the list";
  if (!f.province.trim()) {
    errs.province = countryIsUs ? "Please choose a state" : "Required";
  } else if (countryIsUs && !f.province_code.trim()) {
    errs.province = "Please choose a state from the list";
  }
  if (!countryIsUs && !f.province_code.trim()) {
    errs.province_code = "Required";
  }
  if (!f.zip.trim()) errs.zip = "Required";
  if (!f.phone.trim()) {
    errs.phone = "Required";
  } else {
    const phoneErr = validatePhone(f.phone, country);
    if (phoneErr) errs.phone = phoneErr;
  }
  return errs;
}

function formDiffersFromAddress(
  f: FormState,
  a: ShippingAddress | null,
): boolean {
  if (!a) return true;
  return (
    f.first_name.trim() !== (a.first_name ?? "") ||
    f.last_name.trim() !== (a.last_name ?? "") ||
    f.address1.trim() !== (a.address1 ?? "") ||
    f.address2.trim() !== (a.address2 ?? "") ||
    f.city.trim() !== (a.city ?? "") ||
    f.country.trim() !== (a.country ?? "") ||
    f.country_code.trim() !== (a.country_code ?? "") ||
    f.province.trim() !== (a.province ?? "") ||
    f.province_code.trim() !== (a.province_code ?? "") ||
    f.zip.trim() !== (a.zip ?? "") ||
    f.phone.trim() !== (a.phone ?? "")
  );
}

function formatAddressLines(a: ShippingAddress): string[] {
  const lines: string[] = [];
  const fullName = `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim();
  if (fullName) lines.push(fullName);
  if (a.address1) lines.push(a.address1);
  if (a.address2) lines.push(a.address2);
  const locality = [a.city, a.province, a.zip].filter(Boolean).join(", ");
  if (locality) lines.push(locality);
  if (a.country) lines.push(a.country);
  if (a.phone) lines.push(a.phone);
  return lines;
}

// function tomorrowYmd(): string {
//   const d = new Date();
//   d.setDate(d.getDate() + 1);
//   const y = d.getFullYear();
//   const m = String(d.getMonth() + 1).padStart(2, "0");
//   const day = String(d.getDate()).padStart(2, "0");
//   return `${y}-${m}-${day}`;
// }

export function ReactivateSubscriptionDialog({
  open,
  onOpenChange,
  // productTitle,
  initialAddress,
  onConfirm,
  confirmPending,
  confirmSuccess,
}: ReactivateSubscriptionDialogProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormState>(() =>
    addressToForm(initialAddress),
  );
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>(
    {},
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(addressToForm(initialAddress));
      setIsEditing(!initialAddress);
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [open, initialAddress]);

  // const tomorrowLabel = useMemo(
  //   () => formatSubscriptionDateForDisplay(tomorrowYmd()),
  //   [],
  // );

  const country = useMemo(() => resolveCountry(form), [form]);
  const countryIsUs = isUsCountry(country);

  const errors = useMemo(
    () => computeErrors(form, country, countryIsUs),
    [form, country, countryIsUs],
  );

  const addressChanged = formDiffersFromAddress(form, initialAddress);

  const errorFor = (field: FieldName): string | undefined =>
    touched[field] || submitAttempted ? errors[field] : undefined;

  const markTouched = (field: FieldName) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const setField =
    (key: keyof FormState) =>
    (e: Event & { currentTarget: HTMLInputElement }) => {
      const value = e.currentTarget.value;
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const onCountryValueChange = (value: string) => {
    const match = findCountryByName(value);
    setForm((prev) => {
      const next: FormState = {
        ...prev,
        country: value,
        country_code: match ? match.code : "",
      };
      const wasUs =
        prev.country_code === "US" || /united states/i.test(prev.country);
      const nowUs = match?.code === "US";
      if (wasUs && !nowUs) {
        next.province = "";
        next.province_code = "";
      }
      return next;
    });
  };

  const onUsStateValueChange = (value: string) => {
    const match = findUsStateByName(value);
    setForm((prev) => ({
      ...prev,
      province: value,
      province_code: match ? match.code : "",
    }));
  };

  const handleConfirm = () => {
    if (confirmPending) return;
    if (Object.keys(errors).length > 0) {
      setSubmitAttempted(true);
      return;
    }
    const payload: { address?: StayAiShippingAddressInput } = {};
    if (addressChanged) {
      payload.address = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        address1: form.address1.trim(),
        address2: form.address2.trim() || null,
        city: form.city.trim(),
        country: form.country.trim(),
        country_code: form.country_code.trim() || null,
        province: form.province.trim(),
        province_code: form.province_code.trim(),
        zip: form.zip.trim(),
        phone: form.phone.trim(),
      };
    }
    onConfirm(payload);
  };

  if (confirmSuccess) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={onOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
            <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-4">
              <CircleCheck
                className="tw:size-10 tw:text-[#523D33]"
                strokeWidth={1.5}
              />
            </div>
            <p
              className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-2"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Subscription reactivated
            </p>
            <p className="tw:text-sm tw:m-0 tw:text-gray-500 tw:max-w-70">
              Your upcoming order ships tomorrow.
            </p>
          </div>
          <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2 tw:border-t tw:border-[#FDEDEE]/50">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="tw:w-full tw:box-border tw:rounded-md tw:bg-[#FAC8CA] tw:border-[#FAC8CA] tw:text-[#523D33] hover:tw:bg-[#FAC8CA]/80 tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4 tw:max-h-[90dvh] tw:flex tw:flex-col"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Re-subscribe
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="tw:overflow-y-auto tw:min-h-0 tw:pr-1">
          <p
            className="tw:text-sm tw:my-2"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            Confirm shipping address.
          </p>

          <div className="tw:mt-3 tw:rounded-md tw:border tw:border-[#FDEDEE] tw:bg-[#FDEDEE]/30 tw:p-3">
            <div className="tw:flex tw:items-center tw:justify-between tw:mb-2">
              <p
                className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:m-0"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                SHIPPING ADDRESS{" "}
              </p>
              {!isEditing && initialAddress ? (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-blue-500 hover:tw:text-blue-700 tw:cursor-pointer tw:bg-transparent tw:border-none tw:p-0"
                >
                  <Pencil className="tw:size-3" />
                  Edit
                </button>
              ) : null}
            </div>

            {!isEditing && initialAddress ? (
              <div
                className="tw:text-sm tw:space-y-0.5"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {formatAddressLines(initialAddress).map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            ) : (
              <div className="tw:grid tw:grid-cols-2 tw:gap-2">
                <InputField
                  label="First name"
                  value={form.first_name}
                  onInput={setField("first_name")}
                  onBlur={() => markTouched("first_name")}
                  error={errorFor("first_name")}
                  required
                />
                <InputField
                  label="Last name"
                  value={form.last_name}
                  onInput={setField("last_name")}
                  onBlur={() => markTouched("last_name")}
                  error={errorFor("last_name")}
                  required
                />
                <InputField
                  label="Address line 1"
                  value={form.address1}
                  onInput={setField("address1")}
                  onBlur={() => markTouched("address1")}
                  error={errorFor("address1")}
                  required
                  className="tw:col-span-2"
                />
                <InputField
                  label="Address line 2 (optional)"
                  value={form.address2}
                  onInput={setField("address2")}
                  className="tw:col-span-2"
                />
                <SuggestionField
                  label="Country"
                  required
                  className="tw:col-span-2"
                  value={form.country}
                  onValueChange={onCountryValueChange}
                  onBlur={() => markTouched("country")}
                  error={errorFor("country")}
                  items={COUNTRY_ITEMS}
                  autoComplete="off"
                  ariaLabel="Country"
                />
                <InputField
                  label="City"
                  value={form.city}
                  onInput={setField("city")}
                  onBlur={() => markTouched("city")}
                  error={errorFor("city")}
                  required
                />
                <InputField
                  label="Zip"
                  value={form.zip}
                  onInput={setField("zip")}
                  onBlur={() => markTouched("zip")}
                  error={errorFor("zip")}
                  required
                />
                {countryIsUs ? (
                  <SuggestionField
                    label="State"
                    required
                    className="tw:col-span-2"
                    value={form.province}
                    onValueChange={onUsStateValueChange}
                    onBlur={() => markTouched("province")}
                    error={errorFor("province")}
                    items={US_STATE_ITEMS}
                    autoComplete="off"
                    ariaLabel="State"
                  />
                ) : (
                  <>
                    <InputField
                      label="Province / State"
                      value={form.province}
                      onInput={setField("province")}
                      onBlur={() => markTouched("province")}
                      error={errorFor("province")}
                      required
                    />
                    <InputField
                      label="Province code"
                      value={form.province_code}
                      onInput={setField("province_code")}
                      onBlur={() => markTouched("province_code")}
                      error={errorFor("province_code")}
                      required
                    />
                  </>
                )}
                <InputField
                  label="Phone"
                  value={form.phone}
                  onInput={setField("phone")}
                  onBlur={() => markTouched("phone")}
                  error={errorFor("phone")}
                  required
                  type="tel"
                  autoComplete="tel"
                  className="tw:col-span-2"
                  placeholder={
                    country
                      ? `+${country.callingCode} …`
                      : "+ country code and number"
                  }
                />
                {initialAddress ? (
                  <button
                    type="button"
                    onClick={() => {
                      setForm(addressToForm(initialAddress));
                      setIsEditing(false);
                      setTouched({});
                      setSubmitAttempted(false);
                    }}
                    className="tw:col-span-2 tw:text-xs tw:text-gray-500 hover:tw:text-gray-700 tw:cursor-pointer tw:bg-transparent tw:border-none tw:p-0 tw:text-left"
                  >
                    Cancel edit
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            disabled={confirmPending}
            onClick={handleConfirm}
            className="tw:w-full tw:rounded-md tw:bg-[#523D33] tw:border-[#523D33] tw:text-[#FAC8CA] hover:tw:bg-[#523D33]/90 tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]"
          >
            {confirmPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Confirm & re-subscribe"
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={confirmPending}
            onClick={() => onOpenChange(false)}
            className={`${OUTLINE_BRAND_BTN_CLASS} tw:disabled:opacity-50`}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InputField({
  label,
  value,
  onInput,
  onBlur,
  error,
  required,
  className,
  autoComplete,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onInput: (e: Event & { currentTarget: HTMLInputElement }) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  className?: string;
  autoComplete?: string;
  type?: string;
  placeholder?: string;
}) {
  const invalid = Boolean(error);
  const borderClass = invalid
    ? "tw:border-red-400 focus:tw:border-red-500"
    : "tw:border-gray-300 focus:tw:border-[#523D33]";
  return (
    <label
      className={`tw:flex tw:flex-col tw:gap-1 tw:text-xs ${className ?? ""}`}
      style={{ color: "var(--so-text, #523D33)" }}
    >
      <span className="tw:opacity-80">
        {label}
        {required ? <span className="tw:text-red-500"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onInput={onInput}
        onBlur={onBlur}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className={`tw:w-full tw:rounded-md tw:border ${borderClass} tw:bg-white tw:px-2 tw:py-1.5 tw:text-sm tw:outline-none`}
      />
      {error ? (
        <span className="tw:text-xs tw:text-red-500">{error}</span>
      ) : null}
    </label>
  );
}

function SuggestionField({
  label,
  value,
  onValueChange,
  onBlur,
  error,
  items,
  required,
  className,
  autoComplete,
  placeholder,
  ariaLabel,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  items: SuggestionItem[];
  required?: boolean;
  className?: string;
  autoComplete?: string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  return (
    <label
      className={`tw:flex tw:flex-col tw:gap-1 tw:text-xs ${className ?? ""}`}
      style={{ color: "var(--so-text, #523D33)" }}
    >
      <span className="tw:opacity-80">
        {label}
        {required ? <span className="tw:text-red-500"> *</span> : null}
      </span>
      <SuggestionInput
        value={value}
        onValueChange={onValueChange}
        onBlur={onBlur}
        invalid={Boolean(error)}
        items={items}
        autoComplete={autoComplete}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
      />
      {error ? (
        <span className="tw:text-xs tw:text-red-500">{error}</span>
      ) : null}
    </label>
  );
}
